import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts'
import { useFormatters } from '../../../contexts/PreferencesContext'
import { ChartWrapper } from '../ChartWrapper'

interface Point {
  month: string
  value: number
  count?: number
}

interface OpenArrearsByDueMonthChartProps {
  data: Point[]
  onChartClick: (chartData: unknown) => void
}

/**
 * Cost → Future: today's open-overdue lines bucketed by the month (or week)
 * they were DUE — an age profile of the live backlog, the sibling of the
 * weekly Arrears Backlog. METRIC-DEFINITIONS W3-T14 (FIX-04): this series used
 * to sit under the "Historical Arrears Trend" name; that chart now plots the
 * frozen month-end snapshots.
 */
export function OpenArrearsByDueMonthChart({ data, onChartClick }: OpenArrearsByDueMonthChartProps) {
  const { formatCurrency, formatCompactCurrency } = useFormatters()
  return (
    <ChartWrapper
      title="Open arrears by month due"
      data={data}
      exportFilename="open-arrears-by-month-due"
      legend={[
        { color: '#ef4444', label: 'Arrears value', dataKey: 'value' },
        { color: '#f59e0b', label: 'PO lines', dataKey: 'count' },
      ]}
    >
      <p className="text-xs text-gray-500 mb-2">Today's open overdue lines, by the month they were due</p>
      <ResponsiveContainer width="100%" height={250}>
        <ComposedChart data={data} onClick={onChartClick} style={{ cursor: 'pointer' }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
          <XAxis dataKey="month" tick={{ fontSize: 11 }} />
          <YAxis yAxisId="value" tick={{ fontSize: 11 }} tickFormatter={(v: number) => formatCompactCurrency(v)} />
          <YAxis yAxisId="lines" orientation="right" tick={{ fontSize: 11 }} />
          <Tooltip
            cursor={{ fill: 'rgba(59, 130, 246, 0.1)' }}
            formatter={(value: number, name: string) => (name === 'PO lines' ? `${value} lines` : formatCurrency(value))}
          />
          <Bar yAxisId="value" dataKey="value" fill="#ef4444" name="Arrears value" radius={[4, 4, 0, 0]} />
          <Line yAxisId="lines" type="monotone" dataKey="count" stroke="#f59e0b" strokeWidth={2} name="PO lines" dot={{ r: 3 }} />
        </ComposedChart>
      </ResponsiveContainer>
    </ChartWrapper>
  )
}
